/* eslint-disable no-underscore-dangle */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { datadogRum } from '@datadog/browser-rum';
import { QueryClient } from '@tanstack/vue-query';

import storageService from '@/services/StorageService';

/**
 * Configuração centralizada do Vue Query
 * Gerencia cache persistente, configurações e tracking
 */

function restoreCacheFromStorage(client: QueryClient) {
  try {
    const cached = storageService.vueQueryCache.get();
    if (!cached?.queries) return false;

    const now = Date.now();
    const cacheAge = now - cached.timestamp;
    const maxAge = Number(appSettings.VUE_APP_VUE_QUERY_STALE_TIME) || 30 * 1000;

    if (cacheAge > maxAge) {
      storageService.vueQueryCache.remove();
      return false;
    }

    // Restaura queries no cache
    let restoredCount = 0;
    cached.queries.forEach((queryData) => {
      if (queryData.state?.data) {
        client.setQueryData(queryData.queryKey, queryData.state.data);
        const query = client.getQueryCache().find({ queryKey: queryData.queryKey });
        if (query) {
          query.state.dataUpdatedAt = queryData.state.dataUpdatedAt;
          restoredCount += 1;
        }
      }
    });

    return restoredCount > 0;
  } catch (_error) {
    storageService.vueQueryCache.remove();
    return false;
  }
}

let persistTimer: number | null = null;
function persistCacheToStorage(client: QueryClient, immediate = false) {
  try {
    const doPersist = () => {
      const queries = client.getQueryCache().getAll()
        // Queries que representam limites/decisoes financeiras podem optar por nao sobreviver a
        // navegacao. Elas continuam usando o TanStack para loading e erro, mas nao vao ao storage.
        .filter((q) => q.state.data !== undefined && q.meta?.persist !== false)
        .map((q) => ({
          queryKey: q.queryKey,
          state: {
            data: q.state.data,
            dataUpdatedAt: q.state.dataUpdatedAt,
          },
        }));

      storageService.vueQueryCache.set({
        timestamp: Date.now(),
        queries,
      });
    };

    if (immediate) {
      doPersist();
    } else {
      if (persistTimer) clearTimeout(persistTimer);
      persistTimer = window.setTimeout(doPersist, 500);
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Erro ao persistir cache:', error);
  }
}

/** Lê um número do `appSettings` preservando o `0` (que `||` descartaria como falsy). */
function numeroConfigurado(valor: unknown, padrao: number): number {
  const numero = Number(valor);
  return Number.isFinite(numero) && valor !== '' && valor !== null && valor !== undefined ? numero : padrao;
}

/**
 * Instância singleton do QueryClient
 * Pode ser acessada de qualquer lugar sem precisar de contexto de injeção
 */
let queryClientInstance: QueryClient | null = null;

/**
 * Obtém a instância singleton do QueryClient
 * Cria uma nova instância se ainda não existir
 */
export function getQueryClientInstance(): QueryClient {
  if (!queryClientInstance) {
    queryClientInstance = new QueryClient({
      defaultOptions: {
        queries: {
          staleTime: Number(appSettings.VUE_APP_VUE_QUERY_STALE_TIME) || 30 * 1000, // Dados frescos
          gcTime: Number(appSettings.VUE_APP_VUE_QUERY_GC_TIME) || 30 * 1000, // Mantém em memória
          retry: Number(appSettings.VUE_APP_VUE_QUERY_RETRY) || 0,
          refetchOnWindowFocus: appSettings.VUE_APP_VUE_QUERY_REFETCH_ON_WINDOW_FOCUS === 'true',
          refetchOnReconnect: appSettings.VUE_APP_VUE_QUERY_REFETCH_ON_RECONNECT === 'true',
          refetchOnMount: appSettings.VUE_APP_VUE_QUERY_REFETCH_ON_MOUNT === 'true',
        },
        mutations: {
          // `|| 1` transformava um `0` configurado em 1: desligar o retry de mutação era impossível.
          retry: numeroConfigurado(appSettings.VUE_APP_VUE_QUERY_MUTATION_RETRY, 1),
        },
      },
    });

    restoreCacheFromStorage(queryClientInstance);

    queryClientInstance.getQueryCache().subscribe((event) => {
      if (event?.type === 'updated') {
        const { query } = event;
        if (queryClientInstance) {
          persistCacheToStorage(queryClientInstance);
        }
        if (appSettings.VUE_APP_AMBIENTE === 'prd') {
          const now = Date.now();
          const dataAge = now - (query.state.dataUpdatedAt || now);
          const cacheHit = dataAge > 1000;
          datadogRum.addAction('vue_query_cache', {
            queryKey: JSON.stringify(query.queryKey),
            cacheHit,
            dataAge,
          });
        }
      }
    });

    window.addEventListener('beforeunload', () => {
      if (queryClientInstance) {
        persistCacheToStorage(queryClientInstance, true);
      }
    });
  }

  return queryClientInstance;
}

/**
 * Função para invalidar completamente o cache do Vue Query
 * Remove tanto o cache em memória quanto o cache persistido no storage
 */
export async function invalidateVueQueryCache(): Promise<void> {
  try {
    storageService.vueQueryCache.remove();
    storageService.exibiuLoadingPosLogin.remove();

    // Invalida cache em memória do Vue Query usando singleton
    const queryClient = getQueryClientInstance();

    // Cancela fetches em andamento (ex.: syncAll dívidas) antes de limpar o cache
    await queryClient.cancelQueries({ queryKey: ['dividas'] });
    queryClient.clear();
    await queryClient.invalidateQueries();
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Erro ao invalidar cache do Vue Query:', error);
  }
}

/**
 * Configura e inicializa o QueryClient com cache persistente
 * Mantido para compatibilidade com main.ts
 */
export default function createQueryClient(): QueryClient {
  return getQueryClientInstance();
}
